import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { Divider } from '../divider';
import { PaginationEllipsis } from './pagination-ellipsis';
import { PaginationItem } from './pagination-item';
import { PaginationJumpToFirstButton } from './pagination-jump-to-first-button';
import { PaginationJumpToLastButton } from './pagination-jump-to-last-button';
import { PaginationList } from './pagination-list';
import { PaginationNextButton } from './pagination-next-button';
import { PaginationPageButton } from './pagination-page-button';
import { PaginationPageItems } from './pagination-page-items';
import { PaginationPreviousButton } from './pagination-previous-button';
import { PaginationReadout } from './pagination-readout';
import { PaginationRoot } from './pagination-root';
import type { PaginationPropsInterface } from './types/pagination.types';

/**
 * @since 7.42.0
 */
export const Pagination = forwardRef<HTMLDivElement, PaginationPropsInterface>(
  (props: PaginationPropsInterface, forwardedRef) => {
    const { divider = false, showFirstLastButtons = false, ...rest } = props;
    return (
      <Pagination.Root ref={forwardedRef} showFirstLastButtons={showFirstLastButtons} {...rest}>
        {divider ? <Divider /> : null}
        <Pagination.List>
          {showFirstLastButtons ? (
            <Pagination.Item>
              <Pagination.JumpToFirstButton />
            </Pagination.Item>
          ) : null}
          <Pagination.Item>
            <Pagination.PreviousButton />
          </Pagination.Item>
          <Pagination.PageItems />
          <Pagination.Readout />
          <Pagination.Item>
            <Pagination.NextButton />
          </Pagination.Item>
          {showFirstLastButtons ? (
            <Pagination.Item>
              <Pagination.JumpToLastButton />
            </Pagination.Item>
          ) : null}
        </Pagination.List>
      </Pagination.Root>
    );
  },
) as ForwardRefExoticComponent<PaginationPropsInterface> & {
  Root: typeof PaginationRoot;
  List: typeof PaginationList;
  Item: typeof PaginationItem;
  PageItems: typeof PaginationPageItems;
  NextButton: typeof PaginationNextButton;
  PreviousButton: typeof PaginationPreviousButton;
  PageButton: typeof PaginationPageButton;
  Readout: typeof PaginationReadout;
  JumpToFirstButton: typeof PaginationJumpToFirstButton;
  JumpToLastButton: typeof PaginationJumpToLastButton;
  Ellipsis: typeof PaginationEllipsis;
};

Pagination.Root = PaginationRoot;
Pagination.Item = PaginationItem;
Pagination.List = PaginationList;
Pagination.PageItems = PaginationPageItems;
Pagination.NextButton = PaginationNextButton;
Pagination.PreviousButton = PaginationPreviousButton;
Pagination.PageButton = PaginationPageButton;
Pagination.Readout = PaginationReadout;
Pagination.JumpToFirstButton = PaginationJumpToFirstButton;
Pagination.JumpToLastButton = PaginationJumpToLastButton;
Pagination.Ellipsis = PaginationEllipsis;

Pagination.displayName = 'Pagination';
